import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import LaunchIcon from '@mui/icons-material/Launch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import ShareIcon from '@mui/icons-material/Share';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import WarningIcon from '@mui/icons-material/Warning';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import { fetchProspectFitReport, shareProspectProjections, acceptProspect, declineProspect, updateProspect } from '@src/api/actions/prospects';

const fmtNum = (n) => (n != null ? n.toLocaleString() : '—');
const fmtCurrency = (n) => (n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—');
const fmtPct = (n) => (n != null ? `${n}%` : '—');

const SIGNAL_CONFIG = {
  positive: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  neutral: { color: 'info', icon: <InfoIcon fontSize="small" /> },
  warning: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
  negative: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function ProspectFitReportPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchProspectFitReport(token)
      .then((res) => setReport(res.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Failed to load fit report'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const { prospect, market_size, overlap, new_lanes, budget_assessment, demographics, projections, enrichment, fit_signals } = report;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(paths.prospects)} size="small">
          Prospects
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {prospect.company_name}
            {prospect.is_group && (
              <Chip label={`${prospect.group_brands.length} brands`} size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {prospect.contact_name} · {prospect.contact_email}
            {prospect.phone ? ` · ${prospect.phone}` : ''}
            {' · Submitted '}
            {prospect.created_at ? new Date(prospect.created_at).toLocaleDateString() : '—'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={prospect.status.toUpperCase()} size="small" variant="outlined" />
          {prospect.website && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`}
              target="_blank"
              rel="noopener"
            >
              Website
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Fit Signals — top banner */}
      <FitSignalsCard signals={fit_signals} />

      {/* Decision buttons */}
      <DecisionCard prospect={prospect} onStatusChange={(newStatus) => setReport((r) => ({ ...r, prospect: { ...r.prospect, status: newStatus } }))} />

      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        {/* Left column */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            <MarketSizeCard data={market_size} />
            <OverlapCard data={overlap} />
            <NewLanesCard lanes={new_lanes} />
          </Stack>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            {enrichment && <EnrichmentCard data={enrichment} />}
            <BudgetCard data={budget_assessment} />
            {projections && <ProjectionsCard data={projections} prospectToken={prospect.token} />}
            {prospect.is_group && <GroupBrandsCard brands={prospect.group_brands} />}
            {demographics && <DemographicsCard data={demographics} />}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Fit Signals
// ---------------------------------------------------------------------------
function FitSignalsCard({ signals }) {
  if (!signals?.length) return null;
  return (
    <Card sx={{ mb: 0 }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Fit Assessment</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {signals.map((s, i) => {
            const cfg = SIGNAL_CONFIG[s.type] || SIGNAL_CONFIG.neutral;
            return (
              <Tooltip key={i} title={s.detail} arrow>
                <Chip icon={cfg.icon} label={s.label} color={cfg.color} variant="outlined" size="small" />
              </Tooltip>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Decision (Accept / Decline)
// ---------------------------------------------------------------------------
const DECLINE_REASONS = [
  { value: 'budget_too_low', label: 'Budget below minimum' },
  { value: 'market_too_small', label: 'Market too small' },
  { value: 'client_overlap', label: 'Too much client overlap' },
  { value: 'other', label: 'Other' },
];

function DecisionCard({ prospect, onStatusChange }) {
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  const isDecided = prospect.status === 'contacted' || prospect.status === 'converted' || prospect.status === 'declined';

  const handleReset = async () => {
    try {
      await updateProspect(prospect.token, { status: 'new' });
      onStatusChange('new');
      toast.success('Status reset to New');
    } catch {
      toast.error('Failed to reset status');
    }
  };

  if (isDecided) {
    const color = prospect.status === 'declined' ? 'warning' : 'success';
    const label = prospect.status === 'declined' ? 'Declined' : prospect.status === 'contacted' ? 'Discovery Call Sent' : 'Converted';
    return (
      <Card sx={{ mt: 2, mb: 0 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Chip label={label} color={color} />
              <Typography variant="body2" color="text.secondary">Decision has been made for this prospect.</Typography>
            </Stack>
            {prospect.status !== 'converted' && (
              <Button size="small" variant="text" color="inherit" onClick={handleReset} sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}>
                Undo
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await acceptProspect(prospect.token);
      toast.success('Discovery call invite sent to prospect!');
      onStatusChange('contacted');
    } catch {
      toast.error('Failed to send Calendly link');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason) {
      toast.error('Please select a reason');
      return;
    }
    setDeclining(true);
    try {
      await declineProspect(prospect.token, declineReason);
      toast.success('Decline email sent');
      setShowDecline(false);
      onStatusChange('declined');
    } catch {
      toast.error('Failed to send decline email');
    } finally {
      setDeclining(false);
    }
  };

  return (
    <>
      <Card sx={{ mt: 2, mb: 0 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600}>Decision</Typography>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                color="success"
                startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={handleAccept}
                disabled={accepting}
                sx={{ textTransform: 'none' }}
              >
                Reach Out for Discovery Call
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<ThumbDownIcon />}
                onClick={() => setShowDecline(true)}
                sx={{ textTransform: 'none' }}
              >
                Not a Fit
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Decline dialog */}
      <Dialog open={showDecline} onClose={() => setShowDecline(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Not a Fit</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the primary reason. The prospect will receive a polite email letting them know we're unable to move forward at this time.
          </DialogContentText>
          <FormControl fullWidth size="small">
            <InputLabel>Reason</InputLabel>
            <Select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} label="Reason">
              {DECLINE_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(declineReason === 'budget_too_low' || declineReason === 'market_too_small') && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              The email will mention our upcoming cost-per-lead model and bidding platform, and let them know we'll be in touch when it launches.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDecline(false)}>Cancel</Button>
          <Button
            onClick={handleDecline}
            variant="contained"
            color="warning"
            disabled={declining || !declineReason}
          >
            {declining ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Market Size
// ---------------------------------------------------------------------------
function MarketSizeCard({ data }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Market Size</Typography>
        <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>{fmtNum(data.total_population)}</Typography>
            <Typography variant="body2" color="text.secondary">Total Population</Typography>
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={700}>{fmtNum(data.total_zip_count)}</Typography>
            <Typography variant="body2" color="text.secondary">ZIP Codes</Typography>
          </Box>
        </Stack>
        {data.by_territory?.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Territory</TableCell>
                <TableCell align="right">Population</TableCell>
                <TableCell align="right">ZIPs</TableCell>
                <TableCell>States</TableCell>
                <TableCell>DMAs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.by_territory.map((t) => (
                <TableRow key={t.territory}>
                  <TableCell>{t.territory || 'Unassigned'}</TableCell>
                  <TableCell align="right">{fmtNum(t.population)}</TableCell>
                  <TableCell align="right">{t.zip_count}</TableCell>
                  <TableCell>{t.states?.join(', ') || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.dmas?.join(', ') || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Overlap
// ---------------------------------------------------------------------------
function OverlapCard({ data }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Client Overlap</Typography>
          <Chip
            label={`${data.overall_overlap_pct}% overall`}
            size="small"
            color={data.overall_overlap_pct > 50 ? 'error' : data.overall_overlap_pct > 20 ? 'warning' : 'success'}
            variant="outlined"
          />
        </Stack>

        {data.by_client?.length === 0 ? (
          <Alert severity="success" variant="outlined">
            No overlap with any active clients — this is a clean market.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Verticals</TableCell>
                <TableCell align="right">Overlapping ZIPs</TableCell>
                <TableCell align="right">Overlap</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.by_client.map((c) => (
                <TableRow key={c.client_slug}>
                  <TableCell>
                    <Link href={`/clients/${c.client_slug}`} underline="hover" fontWeight={500}>
                      {c.client_name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                      {c.overlapping_verticals.map((v) => (
                        <Chip key={v} label={v} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{c.overlapping_zip_count}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={fmtPct(c.overlap_pct)}
                      size="small"
                      color={c.overlap_pct > 30 ? 'error' : c.overlap_pct > 10 ? 'warning' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// New Lanes
// ---------------------------------------------------------------------------
function NewLanesCard({ lanes }) {
  if (!lanes?.length) return null;
  const newCount = lanes.filter((l) => l.is_new).length;
  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Lanes</Typography>
          {newCount > 0 && (
            <Chip label={`${newCount} new lane${newCount > 1 ? 's' : ''}`} size="small" color="success" variant="outlined" />
          )}
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vertical</TableCell>
              <TableCell>Territory</TableCell>
              <TableCell align="right">ZIPs</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lanes.map((l, i) => (
              <TableRow key={i}>
                <TableCell>{l.vertical}</TableCell>
                <TableCell>{l.territory || 'Unassigned'}</TableCell>
                <TableCell align="right">{l.zip_count}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={l.is_new ? 'New' : 'Existing'}
                    size="small"
                    color={l.is_new ? 'success' : 'default'}
                    variant={l.is_new ? 'filled' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Company Intel (website scrape + LinkedIn)
// ---------------------------------------------------------------------------
function EnrichmentCard({ data }) {
  if (!data) return null;
  const site = data.website || {};
  const linkedin = data.linkedin || {};
  const hasAnything = site.title || site.description || site.services?.length || Object.keys(linkedin).length;
  if (!hasAnything) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Company Intel</Typography>

        {/* Website info */}
        {(site.title || site.description) && (
          <Box sx={{ mb: 2 }}>
            {site.logo_url && (
              <Box sx={{ mb: 1.5 }}>
                <img src={site.logo_url} alt="Logo" style={{ maxHeight: 40, maxWidth: 200, objectFit: 'contain' }} />
              </Box>
            )}
            {site.title && <Typography fontWeight={600} variant="body2">{site.title}</Typography>}
            {site.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{site.description}</Typography>
            )}
          </Box>
        )}

        {/* About / page content summary */}
        {site.about && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>From Their Website</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontSize: 13, lineHeight: 1.6 }}>{site.about}</Typography>
          </Paper>
        )}

        {/* Services found */}
        {site.services?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Services Detected</Typography>
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {site.services.map((s) => (
                <Chip key={s} label={s} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}

        {/* Service areas */}
        {site.service_areas?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Service Areas Mentioned</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{site.service_areas.join('; ')}</Typography>
          </Box>
        )}

        {/* Phone from website */}
        {site.phone && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Phone (from site)</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{site.phone}</Typography>
          </Box>
        )}

        {/* Social links */}
        {site.social_links && Object.keys(site.social_links).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Social Profiles</Typography>
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {Object.entries(site.social_links).map(([name, url]) => (
                <Chip
                  key={name}
                  label={name.replace('_', ' ')}
                  size="small"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener"
                  clickable
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* LinkedIn */}
        {(linkedin.company_url || linkedin.contact_url || linkedin.company_search_url || linkedin.contact_search_url) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>LinkedIn</Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {linkedin.company_url ? (
                <Link href={linkedin.company_url} target="_blank" rel="noopener" underline="hover" variant="body2">
                  Company Profile
                </Link>
              ) : linkedin.company_search_url ? (
                <Link href={linkedin.company_search_url} target="_blank" rel="noopener" underline="hover" variant="body2" color="text.secondary">
                  Search Company on LinkedIn
                </Link>
              ) : null}
              {linkedin.contact_url ? (
                <Link href={linkedin.contact_url} target="_blank" rel="noopener" underline="hover" variant="body2">
                  Contact Profile
                </Link>
              ) : linkedin.contact_search_url ? (
                <Link href={linkedin.contact_search_url} target="_blank" rel="noopener" underline="hover" variant="body2" color="text.secondary">
                  Search Contact on LinkedIn
                </Link>
              ) : null}
            </Stack>
          </>
        )}

        {/* Brand color swatch */}
        {site.brand_color && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: site.brand_color, border: '1px solid #ddd' }} />
            <Typography variant="caption" color="text.secondary">Brand color: {site.brand_color}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Budget Assessment
// ---------------------------------------------------------------------------
function BudgetCard({ data }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Budget Assessment</Typography>
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{fmtCurrency(data.total_budget)}</Typography>
            <Typography variant="body2" color="text.secondary">Total Monthly Budget</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              label={data.meets_total_minimum ? 'Meets minimum' : `Below ${fmtCurrency(data.total_minimum)} minimum`}
              color={data.meets_total_minimum ? 'success' : 'error'}
              size="small"
              variant="outlined"
            />
          </Box>
        </Stack>
        {data.by_vertical?.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vertical</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell align="right">Minimum</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.by_vertical.map((v) => (
                <TableRow key={v.vertical}>
                  <TableCell>{v.vertical}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.budget)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.minimum)}</TableCell>
                  <TableCell align="center">
                    {v.meets_minimum ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <ErrorIcon fontSize="small" color="error" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Projections (what the client saw)
// ---------------------------------------------------------------------------
function ProjectionsCard({ data, prospectToken }) {
  if (!data) return null;
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const verts = data.verticals || [];
  const totalLeads = verts.reduce((s, v) => s + (v.leads || 0), 0);
  const totalRevenue = verts.reduce((s, v) => s + (v.net_revenue || 0), 0);
  const totalSold = verts.reduce((s, v) => s + (v.net_sold || 0), 0);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await shareProspectProjections(prospectToken);
      const url = res.data.share_url;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success('Projections link copied to clipboard!');
    } catch {
      toast.error('Failed to generate share link');
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Projections (Client View)</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {data.scenario && (
              <Chip label={`${data.scenario} scenario`} size="small" variant="outlined" />
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={sharing ? <CircularProgress size={14} /> : <ShareIcon />}
              onClick={handleShare}
              disabled={sharing}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              Share
            </Button>
          </Stack>
        </Stack>

        {shareUrl && (
          <Alert
            severity="success"
            variant="outlined"
            sx={{ mb: 2 }}
            action={
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
                Copy
              </Button>
            }
          >
            <Link href={shareUrl} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 13, wordBreak: 'break-all' }}>
              {shareUrl}
            </Link>
          </Alert>
        )}

        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{Math.round(totalLeads)}</Typography>
            <Typography variant="body2" color="text.secondary">Est. Leads/mo</Typography>
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>{fmtCurrency(totalRevenue)}</Typography>
            <Typography variant="body2" color="text.secondary">Est. Revenue/mo</Typography>
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>{Math.round(totalSold * 10) / 10}</Typography>
            <Typography variant="body2" color="text.secondary">Est. Net Sold/mo</Typography>
          </Box>
        </Stack>

        {verts.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vertical</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell align="right">CPL</TableCell>
                <TableCell align="right">Leads</TableCell>
                <TableCell align="right">Set Appts</TableCell>
                <TableCell align="right">Net Sold</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verts.map((v) => (
                <TableRow key={v.name || v.id}>
                  <TableCell>{v.name}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.budget)}</TableCell>
                  <TableCell align="right">{v.scenario_cpl ? fmtCurrency(v.scenario_cpl) : v.base_cpl ? fmtCurrency(v.base_cpl) : '—'}</TableCell>
                  <TableCell align="right">{Math.round(v.leads)}</TableCell>
                  <TableCell align="right">{Math.round(v.set_appointments)}</TableCell>
                  <TableCell align="right">{(Math.round(v.net_sold * 10) / 10)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.net_revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Group Brands (multi-brand)
// ---------------------------------------------------------------------------
function GroupBrandsCard({ brands }) {
  if (!brands?.length) return null;
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Brands ({brands.length})</Typography>
        <Stack spacing={2}>
          {brands.map((b) => (
            <Paper key={b.token} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={600}>{b.brand_name}</Typography>
                <Typography variant="body2" color="text.secondary">{fmtCurrency(b.budget)}/mo</Typography>
              </Stack>
              <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                {(b.verticals || []).map((v) => (
                  <Chip key={v} label={v} size="small" variant="outlined" />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">{b.zip_count} ZIP codes</Typography>
            </Paper>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Demographics (from snapshot)
// ---------------------------------------------------------------------------
function DemographicsCard({ data }) {
  const territories = data?.territories || [];
  const totals = data?.totals || {};
  if (!territories.length && !totals.population) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Demographics Summary</Typography>
        {totals.population != null && (
          <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Population</Typography>
              <Typography fontWeight={600}>{fmtNum(totals.population)}</Typography>
            </Box>
            {totals.population_filtered != null && (
              <Box>
                <Typography variant="body2" color="text.secondary">After Filters</Typography>
                <Typography fontWeight={600}>{fmtNum(totals.population_filtered)}</Typography>
              </Box>
            )}
            {totals.population_retained_pct != null && (
              <Box>
                <Typography variant="body2" color="text.secondary">Retained</Typography>
                <Typography fontWeight={600}>{fmtPct(totals.population_retained_pct)}</Typography>
              </Box>
            )}
          </Stack>
        )}
        {territories.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Territory</TableCell>
                <TableCell align="right">Population</TableCell>
                <TableCell align="right">Filtered</TableCell>
                <TableCell align="right">Median Income</TableCell>
                <TableCell align="right">Median Home Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {territories.map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{t.territory || t.vertical || '—'}</TableCell>
                  <TableCell align="right">{fmtNum(t.population)}</TableCell>
                  <TableCell align="right">{fmtNum(t.population_filtered)}</TableCell>
                  <TableCell align="right">{t.median_income ? fmtCurrency(t.median_income) : '—'}</TableCell>
                  <TableCell align="right">{t.median_home_value ? fmtCurrency(t.median_home_value) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
